import { HUD_COPY } from "../config/identity";
import type { OrbitForgeRuntime } from "../game/runtime/runtime";
import { updateHud, type HudRefs } from "./hud-render";
import { createGameIcon, createIcon, type GameIconName, type UiIconName } from "./icon";
import { createAutoLaunchPanel } from "./panels/auto-launch";
import { createCosmeticsPanel } from "./panels/cosmetics";
import { createCratesPanel } from "./panels/crates";
import { createForgePanel } from "./panels/forge";
import { createProfilePanel } from "./panels/profile";

interface RailAction {
  readonly id: string;
  readonly label: string;
  readonly icon: GameIconName;
  readonly unlockLevel: number;
  readonly readyState: string;
}

const leftRailActions: readonly RailAction[] = [
  { id: "forge", label: HUD_COPY.forge, icon: "forge", unlockLevel: 1, readyState: "" },
  { id: "auto", label: HUD_COPY.auto, icon: "autoLaunch", unlockLevel: 2, readyState: "" },
  { id: "crates", label: HUD_COPY.crates, icon: "supplyCrates", unlockLevel: 2, readyState: "" }
];

const rightRailActions: readonly RailAction[] = [
  { id: "overdrive", label: HUD_COPY.overdrive, icon: "overdrive", unlockLevel: 3, readyState: "ready" },
  { id: "storm", label: HUD_COPY.storm, icon: "reactorStorm", unlockLevel: 5, readyState: "ready" }
];

export function createHud(runtime: OrbitForgeRuntime): HTMLElement {
  const hud = document.createElement("div");
  hud.className = "of-hud";

  const refs: HudRefs = {
    level: document.createElement("span"),
    title: document.createElement("span"),
    credits: document.createElement("span"),
    activeCores: document.createElement("span"),
    dailyState: document.createElement("span"),
    lanes: [],
    railStates: new Map(),
    railButtons: new Map(),
    panelTargets: new Map(),
    feedbackTargets: new Map()
  };

  hud.append(
    createTopLeftCluster(refs, runtime),
    createTopCenterCluster(refs),
    createUtilityCluster(refs, runtime),
    createRail("of-left-rail", leftRailActions, refs, runtime),
    createRail("of-right-rail", rightRailActions, refs, runtime),
    createProfilePanel(runtime),
    createAutoLaunchPanel(runtime),
    createForgePanel(runtime),
    createCratesPanel(runtime),
    createCosmeticsPanel(runtime),
    createCollector(refs)
  );

  let seenFeedbackId = 0;
  runtime.subscribe((snapshot) => {
    seenFeedbackId = updateHud(refs, snapshot, seenFeedbackId);
  });
  return hud;
}

function createTopLeftCluster(refs: HudRefs, runtime: OrbitForgeRuntime): HTMLElement {
  const cluster = document.createElement("div");
  cluster.className = "of-top-left";

  const profile = document.createElement("button");
  profile.className = "of-profile-chip";
  profile.type = "button";
  profile.addEventListener("click", () => runtime.dispatch({ type: "open-panel", panel: "profile" }));
  refs.level.className = "of-value";
  refs.title.className = "of-label";
  profile.append(createGameIcon("profilePlayer", "Profile"), createTextBlock(refs.level, refs.title));
  refs.panelTargets.set("profile", profile);
  refs.feedbackTargets.set("profile", profile);

  const daily = document.createElement("button");
  daily.className = "of-daily-chip";
  daily.type = "button";
  daily.setAttribute("aria-label", "Streak Bonus");
  daily.hidden = true;
  daily.addEventListener("click", () => runtime.dispatch({ type: "claim-daily-ignition" }));
  refs.dailyState.className = "of-label";
  refs.dailyState.textContent = "AVAILABLE";
  daily.append(createTextBlock(createValue(HUD_COPY.daily), refs.dailyState));
  refs.feedbackTargets.set("daily", daily);

  cluster.append(profile, daily);
  return cluster;
}

function createTopCenterCluster(refs: HudRefs): HTMLElement {
  const cluster = document.createElement("div");
  cluster.className = "of-top-center";

  cluster.append(
    createMetric(HUD_COPY.credits, refs.credits),
    createMetric("CORES", refs.activeCores, "active-cores", refs)
  );

  return cluster;
}

function createUtilityCluster(refs: HudRefs, runtime: OrbitForgeRuntime): HTMLElement {
  const cluster = document.createElement("div");
  cluster.className = "of-top-right";
  cluster.append(createIconButton("settings", HUD_COPY.settings, "settings", refs, runtime));
  return cluster;
}

function createRail(className: string, actions: readonly RailAction[], refs: HudRefs, runtime: OrbitForgeRuntime): HTMLElement {
  const rail = document.createElement("div");
  rail.className = className;
  rail.setAttribute("role", "toolbar");
  rail.setAttribute("aria-label", className.includes("left") ? "Progression actions" : "Event actions");

  for (const action of actions) {
    const button = document.createElement("button");
    button.className = "of-rail-button";
    button.type = "button";
    button.dataset.action = action.id;
    button.dataset.actionLabel = action.label;
    button.dataset.readyState = action.readyState;
    button.dataset.unlockLevel = action.unlockLevel.toString();
    button.addEventListener("click", () => {
      if (button.getAttribute("aria-disabled") === "true") {
        runtime.dispatch({ type: "emit-feedback", event: { kind: "denied", target: action.id } });
        return;
      }
      if (action.id === "storm") {
        runtime.dispatch({ type: "start-storm" });
        return;
      }
      if (action.id === "overdrive") {
        if (button.dataset.prestigeOverdrive === "true") {
          runtime.dispatch({ type: "start-prestige-overdrive" });
        } else {
          runtime.dispatch({ type: "emit-feedback", event: { kind: "pulse", target: action.id } });
        }
        return;
      }
      runtime.dispatch({ type: "open-panel", panel: action.id });
    });
    const lockBadge = createIcon("locked", "Locked");
    lockBadge.classList.add("of-rail-lock-badge");
    lockBadge.setAttribute("aria-hidden", "true");
    lockBadge.removeAttribute("aria-label");
    lockBadge.removeAttribute("role");

    const stateLabel = createLabel(action.readyState);
    stateLabel.classList.add("of-rail-state");
    const requirementLine = document.createElement("span");
    requirementLine.className = "of-rail-requirement-line";
    requirementLine.append(lockBadge, stateLabel);
    refs.railButtons.set(action.id, button);
    refs.railStates.set(action.id, stateLabel);
    button.append(createGameIcon(action.icon, action.label), createTextBlock(createValue(action.label), requirementLine));
    refs.feedbackTargets.set(action.id, button);
    rail.append(button);
  }

  return rail;
}

function createCollector(refs: HudRefs): HTMLElement {
  const collector = document.createElement("section");
  collector.className = "of-collector";
  collector.setAttribute("aria-label", "Collector lanes");

  for (let index = 0; index < 5; index += 1) {
    const lane = document.createElement("div");
    lane.className = "of-collector-lane";
    const lockIcon = createIcon("locked", "Locked");
    lockIcon.classList.add("of-collector-lock");
    lockIcon.setAttribute("aria-hidden", "true");
    lockIcon.removeAttribute("aria-label");
    lockIcon.removeAttribute("role");
    const value = document.createElement("span");
    value.className = "of-collector-value";
    value.textContent = "LV 3";
    lane.append(lockIcon, value);
    refs.lanes.push({ root: lane, value });
    collector.append(lane);
  }

  return collector;
}

function createMetric(label: string, value: HTMLElement, feedbackTargetId?: string, refs?: HudRefs): HTMLElement {
  const metric = document.createElement("div");
  metric.className = "of-metric";
  value.className = "of-value";
  metric.append(createLabel(label), value);
  if (feedbackTargetId && refs) {
    refs.feedbackTargets.set(feedbackTargetId, metric);
  }
  return metric;
}

function createTextBlock(primary: HTMLElement, secondary: HTMLElement): HTMLElement {
  const block = document.createElement("span");
  block.className = "of-text-block";
  block.append(primary, secondary);
  return block;
}

function createLabel(text: string): HTMLElement {
  const label = document.createElement("span");
  label.className = "of-label";
  label.textContent = text;
  return label;
}

function createValue(text: string): HTMLElement {
  const value = document.createElement("span");
  value.className = "of-value";
  value.textContent = text;
  return value;
}

function createIconButton(icon: UiIconName, label: string, id: string, refs: HudRefs, runtime: OrbitForgeRuntime): HTMLButtonElement {
  const button = document.createElement("button");
  button.className = "of-icon-button";
  button.type = "button";
  button.title = label;
  button.addEventListener("click", () => runtime.dispatch({ type: "open-panel", panel: id }));
  button.append(createIcon(icon, label));
  refs.panelTargets.set(id, button);
  refs.feedbackTargets.set(id, button);
  return button;
}
